export const seasonalThemes = {
  premium: {
    id: "premium",
    name: "OdontoFlow Premium",
    colors: {
      primary: "#2457D6",
      primaryDark: "#163B84",
      secondary: "#4F46E5",
      accent: "#0EA5E9",

      text: "#0F172A",
      textSoft: "#334155",
      textMuted: "#64748B",

      bgPage: "#F3F6FB",
      bgCard: "#FFFFFF",
      bgSoft: "#F8FAFC",

      border: "#E2E8F0",
      borderStrong: "#CBD5E1",

      success: "#166534",
      successBg: "#F0FDF4",
      successBorder: "#BBF7D0",

      danger: "#991B1B",
      dangerBg: "#FEF2F2",
      dangerBorder: "#FECACA",

      warning: "#C2410C",
      warningBg: "#FFF7ED",
      warningBorder: "#FED7AA",

      info: "#1D4ED8",
      infoBg: "#EFF6FF",
      infoBorder: "#BFDBFE",

      violet: "#7C3AED",
      violetBg: "#F5F3FF",
      violetBorder: "#DDD6FE"
    },
    gradients: {
      hero: "linear-gradient(135deg, #081225 0%, #122B57 56%, #2457D6 100%)",
      heroSoft: "linear-gradient(135deg, #2457D6 0%, #4F46E5 100%)",
      sidebar: "linear-gradient(180deg, #07101E 0%, #0C1831 52%, #0D2044 100%)",
      cta: "linear-gradient(135deg, #2457D6 0%, #4F46E5 100%)"
    },
    decor: {
      heroGlow:
        "radial-gradient(circle at top right, rgba(255,255,255,0.15), transparent 34%)",
      cardGlow: "none"
    }
  },

  spring: {
    id: "spring",
    name: "OdontoFlow Spring",
    colors: {
      primary: "#0F766E",
      primaryDark: "#115E59",
      secondary: "#7C3AED",
      accent: "#22C55E",

      text: "#0F172A",
      textSoft: "#334155",
      textMuted: "#64748B",

      bgPage: "#F5FBFA",
      bgCard: "#FFFFFF",
      bgSoft: "#F0FDFA",

      border: "#DDEEEB",
      borderStrong: "#BEDAD5",

      success: "#166534",
      successBg: "#F0FDF4",
      successBorder: "#BBF7D0",

      danger: "#991B1B",
      dangerBg: "#FEF2F2",
      dangerBorder: "#FECACA",

      warning: "#C2410C",
      warningBg: "#FFF7ED",
      warningBorder: "#FED7AA",

      info: "#0F766E",
      infoBg: "#ECFEFF",
      infoBorder: "#A5F3FC",

      violet: "#7C3AED",
      violetBg: "#F5F3FF",
      violetBorder: "#DDD6FE"
    },
    gradients: {
      hero: "linear-gradient(135deg, #073A37 0%, #0F766E 52%, #22C55E 100%)",
      heroSoft: "linear-gradient(135deg, #14B8A6 0%, #22C55E 100%)",
      sidebar: "linear-gradient(180deg, #042F2E 0%, #0F766E 50%, #14532D 100%)",
      cta: "linear-gradient(135deg, #0F766E 0%, #22C55E 100%)"
    },
    decor: {
      heroGlow:
        "radial-gradient(circle at top right, rgba(255,255,255,0.14), transparent 32%)",
      cardGlow: "none"
    }
  },

  autumn: {
    id: "autumn",
    name: "OdontoFlow Autumn",
    colors: {
      primary: "#9A3412",
      primaryDark: "#7C2D12",
      secondary: "#C2410C",
      accent: "#F59E0B",

      text: "#0F172A",
      textSoft: "#334155",
      textMuted: "#64748B",

      bgPage: "#FFF9F4",
      bgCard: "#FFFFFF",
      bgSoft: "#FFF7ED",

      border: "#FED7AA",
      borderStrong: "#FDBA74",

      success: "#166534",
      successBg: "#F0FDF4",
      successBorder: "#BBF7D0",

      danger: "#991B1B",
      dangerBg: "#FEF2F2",
      dangerBorder: "#FECACA",

      warning: "#C2410C",
      warningBg: "#FFF7ED",
      warningBorder: "#FED7AA",

      info: "#C2410C",
      infoBg: "#FFF7ED",
      infoBorder: "#FED7AA",

      violet: "#7C2D12",
      violetBg: "#FFF7ED",
      violetBorder: "#FED7AA"
    },
    gradients: {
      hero: "linear-gradient(135deg, #4A1708 0%, #9A3412 52%, #F59E0B 100%)",
      heroSoft: "linear-gradient(135deg, #C2410C 0%, #F59E0B 100%)",
      sidebar: "linear-gradient(180deg, #431407 0%, #7C2D12 52%, #A16207 100%)",
      cta: "linear-gradient(135deg, #C2410C 0%, #F59E0B 100%)"
    },
    decor: {
      heroGlow:
        "radial-gradient(circle at top right, rgba(255,255,255,0.13), transparent 31%)",
      cardGlow: "none"
    }
  }
}

export function resolveTheme(themeId) {
  return seasonalThemes[themeId] || seasonalThemes.premium
}

export function getActiveTheme() {
  if (typeof window === "undefined") {
    return seasonalThemes.premium
  }

  const saved = window.localStorage.getItem("odontoflow_theme")
  return resolveTheme(saved)
}

export function setActiveTheme(themeId) {
  if (typeof window === "undefined") return

  const resolved = resolveTheme(themeId)
  window.localStorage.setItem("odontoflow_theme", resolved.id)
  applyThemeToDocument(resolved)
}

export function getSeasonSuggestion(date = new Date()) {
  const month = date.getMonth() + 1

  if ([3, 4, 5].includes(month)) return "spring"
  if ([9, 10, 11].includes(month)) return "autumn"
  return "premium"
}

export function applyThemeToDocument(theme = getActiveTheme()) {
  if (typeof document === "undefined") return theme

  const root = document.documentElement

  const cssVars = {
    "--of-bg-app": theme.colors.bgPage,
    "--of-surface": theme.colors.bgCard,
    "--of-surface-alt": theme.colors.bgSoft,

    "--of-border": theme.colors.border,
    "--of-border-strong": theme.colors.borderStrong,

    "--of-text": theme.colors.text,
    "--of-text-soft": theme.colors.textSoft,
    "--of-text-muted": theme.colors.textMuted,

    "--of-brand-600": theme.colors.primaryDark,
    "--of-brand-500": theme.colors.primary,
    "--of-brand-400": theme.colors.accent,

    "--of-success-bg": theme.colors.successBg,
    "--of-success-border": theme.colors.successBorder,
    "--of-success-text": theme.colors.success,

    "--of-warning-bg": theme.colors.warningBg,
    "--of-warning-border": theme.colors.warningBorder,
    "--of-warning-text": theme.colors.warning,

    "--of-danger-bg": theme.colors.dangerBg,
    "--of-danger-border": theme.colors.dangerBorder,
    "--of-danger-text": theme.colors.danger,

    "--of-info-bg": theme.colors.infoBg,
    "--of-info-border": theme.colors.infoBorder,
    "--of-info-text": theme.colors.info,

    "--of-gradient-hero": theme.gradients.hero,
    "--of-gradient-sidebar": theme.gradients.sidebar,
    "--of-gradient-cta": theme.gradients.cta
  }

  Object.entries(cssVars).forEach(([key, value]) => {
    root.style.setProperty(key, value)
  })

  root.setAttribute("data-of-theme", theme.id)
  return theme
}